import React, { useMemo } from 'react';
import { BillNumberRenderer } from './CellRenderers/BillNumberRenderer';
import { IBillsTableRowProps } from '../types';
import { BILLS_TABLE_COLUMN_KEYS } from '../consts';
import { useStore } from '../../../../../../../store';
import { BILLS_STATUSES } from '../../../../../consts';
import { DueDateRenderer } from './CellRenderers/DueDateRenderer';
import { StatusRenderer } from './CellRenderers/StatusRenderer';
import { ActionRenderer } from './CellRenderers/ActionRenderer';
import { VendorRenderer } from './CellRenderers/VendorRenderer';
import { AttachmentRenderer } from './CellRenderers/AttachmentRenderer';
import { getNumOfOverdueDays } from '../../../../../../../helpers/datesAndTime.helper';
import { BILLS_BODY_CELL_STYLES, WIDGET_BILLS_BODY_CELL_STYLES } from '../styles';
import { AmountRenderer } from './CellRenderers/AmountRenderer';

export const BillsTableRow = (
  {
    columnKeys,
    row,
    isLastRow,
    lastRow,
    isWidgetMode = false,
    onBillRowClick,
  }: IBillsTableRowProps,
) => {
  const setSelectedBillId = useStore(state => state.setSelectedBill);
  const setShowBillDetailsModal = useStore(state => state.setShowBillDetailsModal);
  const isOverDue = useMemo(() => getNumOfOverdueDays(row.dueDate) > 0 && row.status !== BILLS_STATUSES.PAID && !row.paidTime, [row]);
  const renderCellValue = (columnKey: string) => {
    if (columnKey === BILLS_TABLE_COLUMN_KEYS.BILL_NUMBER) return <BillNumberRenderer billNumber={row[columnKey]} createTime={row.createTime} />;
    if (columnKey === BILLS_TABLE_COLUMN_KEYS.VENDOR_NAME) return <VendorRenderer vendorName={row[columnKey]} supplierId={row.supplierId} />;
    if (columnKey === BILLS_TABLE_COLUMN_KEYS.DUE_DATE) return <DueDateRenderer billId={row?.billId} status={row?.calculatedStatus} dueDate={row[columnKey]} paidTime={row?.paidTime} recurringPaymentData={row?.recurringPaymentData} isWidgetMode={isWidgetMode} />;
    if (columnKey === BILLS_TABLE_COLUMN_KEYS.STATUS) return <StatusRenderer status={row[columnKey]} />;
    if (columnKey === BILLS_TABLE_COLUMN_KEYS.ATTACHMENT) return <AttachmentRenderer documentId={row[columnKey]} billId={row?.billId} billStatus={row.calculatedStatus} />;
    if (columnKey === BILLS_TABLE_COLUMN_KEYS.AMOUNT) return <AmountRenderer amount={row[columnKey]} isOverDue={isOverDue} />;
    if (columnKey === BILLS_TABLE_COLUMN_KEYS.ACTION) return <ActionRenderer billId={row?.billId} />;
    return null;
  };

  const renderBottomLeftRadius = (columnKey: string) => (isLastRow && columnKey === BILLS_TABLE_COLUMN_KEYS.BILL_NUMBER ? 'rounded-bl-lg' : '');
  const renderBottomRightRadius = (columnKey: string) => (isLastRow && columnKey === BILLS_TABLE_COLUMN_KEYS.ACTION ? 'rounded-br-lg' : '');
  const renderCell = (columnKey: string) => (
    <td
      id={`td_${columnKey}`}
      key={columnKey}
    >
      <div className={`${isWidgetMode ? WIDGET_BILLS_BODY_CELL_STYLES[columnKey] : BILLS_BODY_CELL_STYLES[columnKey]} ${!isWidgetMode ? renderBottomLeftRadius(columnKey) : ''} ${!isWidgetMode ? renderBottomRightRadius(columnKey) : ''}`}>
        {renderCellValue(columnKey)}
      </div>
    </td>
  );

  const onRowClick = () => {
    setSelectedBillId(row?.billId);
    setShowBillDetailsModal(true);
  };

  return (
    <tr className={`cursor-pointer hover:bg-bw00 ${isWidgetMode ? 'w-full align-top h-1/3' : ''} ${!isLastRow && isWidgetMode ? 'border-bw03 border-b-[1px]' : ''} ${isLastRow && !isWidgetMode ? 'border-b-[1px] border-bw03' : ''}`} onClick={isWidgetMode ? () => onBillRowClick && onBillRowClick(row.billId) : onRowClick} ref={lastRow} data-testid={`BillsHub__table-row__bill-${row.billNumber}`}>
      {columnKeys.map((columnKey: string) => renderCell(columnKey))}
    </tr>
  );
};
